/**
 * DeductionsListView Organism
 * Pure presentation component for a recent-first or grouped deductions list.
 * Follows PaymentsListView pattern with ListViewShell + collapsible groups.
 */

import { useCallback, useMemo } from 'react';
import {
  Alert,
  Collapse,
  LedgerHeader,
  ReceiptLongIcon,
  ledgerContainerClass,
  type LedgerCollapse,
  type LedgerColumn,
} from '@billda/design-system';
import { useShallow } from 'zustand/react/shallow';
import { DeductionListItem } from './DeductionListItem';
import { DeductionGroupHeader } from './DeductionGroupHeader';
import { ListViewShell } from '../../../shared/components/ui/ListViewShell';
import { groupDeductionsByWorker } from '../utils/groupDeductionsByWorker';
import { groupDeductionsBySite } from '../utils/groupDeductionsBySite';
import { useDeductionsUIStore } from '../stores/deductionsUIStore';
import type { AdminDeductionRow } from '@billda/api';

export interface DeductionsListViewProps {
  deductions: AdminDeductionRow[];
  isLoading: boolean;
  error: Error | null;
  onDeductionClick?: (deduction: AdminDeductionRow) => void;
}

const DEDUCTIONS_COLLAPSE: LedgerCollapse = {
  container: 'deductions-collection',
  at: 620,
};

export function DeductionsListView({
  deductions,
  isLoading,
  error,
  onDeductionClick,
}: DeductionsListViewProps) {
  const { collapsedGroups, toggleGroup, groupingMode } = useDeductionsUIStore(
    useShallow(state => ({
      collapsedGroups: state.collapsedGroups,
      toggleGroup: state.toggleGroup,
      groupingMode: state.groupingMode,
    }))
  );

  const groups = useMemo(() => {
    if (groupingMode === 'site') {
      return groupDeductionsBySite(deductions);
    }
    if (groupingMode === 'worker') {
      return groupDeductionsByWorker(deductions);
    }
    return [];
  }, [deductions, groupingMode]);
  const recentDeductions = useMemo(
    () =>
      [...deductions].sort(
        (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)
      ),
    [deductions]
  );
  const columns = useMemo<LedgerColumn[]>(() => {
    const contextColumns: LedgerColumn[] =
      groupingMode === 'none'
        ? [
            {
              key: 'context',
              label: 'Worker / Gang',
              flex: 1.15,
            },
            {
              key: 'sitePlot',
              label: 'Site / Plot',
              flex: 1.15,
              hideBelow: 820,
            },
          ]
        : [
            {
              key: 'context',
              label: groupingMode === 'site' ? 'Worker / Gang' : 'Site / Plot',
              flex: 1.3,
            },
          ];
    return [
      ...contextColumns,
      { key: 'status', label: 'Status', flex: 0.75 },
      {
        key: 'createdBy',
        label: 'Added by',
        flex: 1,
        hideBelow: 900,
      },
      { key: 'when', label: 'Added', flex: 0.8, hideBelow: 760 },
    ];
  }, [groupingMode]);

  const handleToggle = useCallback(
    (groupName: string) => () => toggleGroup(groupName),
    [toggleGroup]
  );

  if (error && !isLoading && deductions.length === 0) {
    return (
      <div className="p-4">
        <Alert severity="error">
          Failed to load deductions. Please try again.
        </Alert>
      </div>
    );
  }

  return (
    <div
      className={`flex h-full min-h-0 flex-col overflow-auto ${ledgerContainerClass(DEDUCTIONS_COLLAPSE.container)}`}
    >
      <ListViewShell
        itemCount={deductions.length}
        loading={isLoading}
        emptyIcon={
          <ReceiptLongIcon
            aria-hidden="true"
            className="size-12 text-content-secondary"
          />
        }
        emptyTitle="No deductions this week"
        emptyHelperText="Deductions like crane hire, scaffolding, and materials show here once they're added to pots in the selected period."
        emptyMessage=""
        data-testid="deductions-list"
      >
        <LedgerHeader
          columns={columns}
          collapse={DEDUCTIONS_COLLAPSE}
          titleLabel="Description"
          valueLabel="Amount"
          showActions
          hideBelow={DEDUCTIONS_COLLAPSE.at}
        />
        {groupingMode === 'none'
          ? recentDeductions.map((deduction, index) => (
              <DeductionListItem
                key={deduction.id}
                deduction={deduction}
                columns={columns}
                collapse={DEDUCTIONS_COLLAPSE}
                groupingMode={groupingMode}
                onClick={onDeductionClick}
                data-testid={`deduction-row-${index}`}
              />
            ))
          : groups.map((group, index) => {
              const isCollapsed = collapsedGroups.has(group.groupName);
              const panelId = `deduction-group-panel-${
                group.groupName
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, '-')
                  .replace(/(^-|-$)/g, '') || index
              }`;
              return (
                <div key={group.groupName}>
                  <DeductionGroupHeader
                    groupName={group.groupName}
                    count={group.count}
                    totalAmount={group.totalAmount}
                    expanded={!isCollapsed}
                    onToggle={handleToggle(group.groupName)}
                    collapse={DEDUCTIONS_COLLAPSE}
                    panelId={panelId}
                    data-testid={`deduction-group-${group.groupName}`}
                  />
                  <Collapse in={!isCollapsed} unmountOnExit>
                    <div id={panelId}>
                      {group.deductions.map((deduction, rowIndex) => (
                        <DeductionListItem
                          key={deduction.id}
                          deduction={deduction}
                          columns={columns}
                          collapse={DEDUCTIONS_COLLAPSE}
                          groupingMode={groupingMode}
                          onClick={onDeductionClick}
                          data-testid={`deduction-row-${rowIndex}`}
                        />
                      ))}
                    </div>
                  </Collapse>
                </div>
              );
            })}
      </ListViewShell>
    </div>
  );
}
